import {
  Dialog,
  Box,
  Container,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Button,
} from '@mui/material';
import SentimentVeryDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentVeryDissatisfiedOutlined';
import { useEffect, useState } from 'react';
import { EventValidationError } from '../../model/event-validation-error';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { loadOverrideReasons, selectError, selectOverrideReasons, selectStatus } from '../../app/slices/overridesSlice';
import { selectCompleteErrorMessage } from '../../app/slices/eventsSlice';
import OverrideReasons from './OverrideReasons';
import { ApiStatus } from '../../api/apiStatus';
import { LoadingButton } from '@mui/lab';
import EventCompletionInfoSubHeader from '../commons/EventCompletionInfoSubHeader';
import EventCompletionInfoHeader from '../commons/EventCompletionInfoHeader';
import { OverrideReasonPayload } from '../../model/override-reason-payload';
import { OverrideReason } from '../../model/override-reason';
import React from 'react';
import EventCompletionFooter from '../commons/EventCompletionFooter';
import ErrorStatusLine from '../commons/ErrorStatusLine';
import EventCompletionContent from '../commons/EventCompletionContent';
import EventCompletionSubContent from '../commons/EventCompletionSubContent';
import UserRole from '../../model/user-role';
import { GROUP_VALIDATION_ERROR_FOR_ADMINS, GROUP_VALIDATION_ERROR_FOR_APPLEAD } from './GroupValiationMessages';

interface IProps {
  validationErrors?: EventValidationError[];
  eventDates?: string;
  eventId?: string;
  userName?: string;
  canCompleteEvent: boolean;
  completeStatus: ApiStatus;
  completeEvent: Function;
  userRole?: UserRole;
  isImmediateCompletionAttempt?: boolean;
}

function EventValidationModal({
  validationErrors,
  eventDates,
  eventId,
  userName,
  completeStatus,
  canCompleteEvent,
  completeEvent,
  userRole,
  isImmediateCompletionAttempt,
}: IProps) {
  const [override, setOverride] = useState(false);
  const [alert, setAlert] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const overridable = validationErrors?.every(
    (it: EventValidationError) => it.roleCustomMessageList?.find((it) => it.role === userRole)?.overridable
  );
  if (!overridable && override) {
    setOverride(false);
  }
  const errorLength = validationErrors?.length || 0;

  const buildOverrideSelections = (reasons: OverrideReason[]): OverrideReasonPayload[] =>
    reasons.map((reason): OverrideReasonPayload => {
      return {
        ...reason,
        selected: false,
        note: '',
      };
    });

  const dispatch = useAppDispatch();
  const overrideReasons = useAppSelector(selectOverrideReasons);
  const status = useAppSelector(selectStatus);
  const errorMessage = useAppSelector(selectError);
  const completeErrorMessage = useAppSelector(selectCompleteErrorMessage);
  const [overrideSelections, setOverrideSelections] = React.useState<OverrideReasonPayload[]>([]);

  useEffect(() => {
    if (completeStatus === 'FAILED' && !completeErrorMessage) {
      setOpenDialog(true);
    }
  }, [completeErrorMessage, completeStatus]);

  useEffect(() => {
    setOverrideSelections(buildOverrideSelections(overrideReasons));
  }, [overrideReasons]);

  const [selectedOverrideReasons, setSelectedOverrideReasons] = React.useState<OverrideReasonPayload[]>([]);

  const setOverrideSelected = (reasonId: string, selected: boolean, requiresNote: boolean) => {
    if (requiresNote) {
      setAlert(false);
    }
    const overrides = overrideSelections.map((override) => {
      if (override.reasonId === reasonId) {
        return { ...override, selected, note: '' } as OverrideReasonPayload;
      }
      return override;
    });
    setOverrideSelections(overrides);
    setSelectedOverrideReasons(overrides.filter((it) => it.selected));
  };

  const setOverrideNote = (reasonId: string, note: string) => {
    setAlert(false);
    const overrides = overrideSelections.map((override) => {
      if (override.reasonId === reasonId) {
        return { ...override, note } as OverrideReasonPayload;
      }
      return override;
    });
    setOverrideSelections(overrides);
    setSelectedOverrideReasons(overrides.filter((it) => it.selected));
  };

  const toggleOverride = () => {
    if (!status && overrideReasons.length === 0) {
      dispatch(loadOverrideReasons());
    }
    setOverride(!override);
  };
  const handleClose = () => {
    setOpenDialog(false);
    setAlert(false);
  };

  const getErrorMessage = (validationError: EventValidationError) => {
    let errorMessage =
      validationError.roleCustomMessageList?.find((it) => it.role === userRole)?.errorMessage ||
      validationError.errorMessage;
    if (errorMessage === 'GROUP_VALIDATION_ERROR_FOR_ADMINS') {
      errorMessage = GROUP_VALIDATION_ERROR_FOR_ADMINS;
    } else if (errorMessage === 'GROUP_VALIDATION_ERROR_FOR_APPLEAD') {
      errorMessage = GROUP_VALIDATION_ERROR_FOR_APPLEAD;
    }
    return errorMessage;
  };

  const handleCompleteEvent = () => {
    const requiredReasonId = overrideReasons?.find((it) => it.requiresNote)?.reasonId;
    const userReason = selectedOverrideReasons.find((it) => it.reasonId === requiredReasonId);

    if (userReason && !userReason.note.trim()) {
      setAlert(true);
      return;
    }
    completeEvent(
      {
        eventManagerUsername: userName,
        overrideReasons: selectedOverrideReasons,
      },
      'USER_FORCE_COMPLETE'
    );
    handleClose();
  };

  return (
    <Dialog open={openDialog} fullWidth={true} maxWidth="md" onClose={handleClose}>
      <EventCompletionInfoHeader title="Event Validation" onClose={handleClose} />
      <Container>
        <Typography sx={{ mt: 2 }} variant="h6" color="primary.dark">
          We have found ({errorLength}) possible issue{errorLength > 1 && 's'} with this event.
        </Typography>
        <EventCompletionInfoSubHeader eventDates={eventDates} eventId={eventId} userName={userName} />
        <EventCompletionContent>
          <EventCompletionSubContent title="Event Status:">
            {validationErrors?.map((err) => (
              <ErrorStatusLine key={err.errorMessage} message={getErrorMessage(err)} />
            ))}

            {overridable && (
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={override} onChange={toggleOverride} />}
                  label={
                    <Typography variant="h4" sx={{ fontWeight: 'normal' }}>
                      <b>Override error{errorLength > 1 && 's'}?</b> Please note: This will complete this event as-is,
                      without the missing data.
                    </Typography>
                  }
                />
              </FormGroup>
            )}
          </EventCompletionSubContent>

          {override && (
            <EventCompletionSubContent title="Why was an override needed? (Required)">
              {status === 'LOADING' && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <CircularProgress size="2em" />
                </Box>
              )}
              {status === 'SUCCESS' && (
                <OverrideReasons
                  alert={alert}
                  reasons={overrideReasons}
                  selections={overrideSelections}
                  setSelected={setOverrideSelected}
                  setNote={setOverrideNote}
                />
              )}
              {status === 'FAILED' && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <SentimentVeryDissatisfiedOutlinedIcon color="error" />
                  <Typography variant="h4" color="error">
                    Failed to load override reasons: {errorMessage}
                  </Typography>
                </Box>
              )}
            </EventCompletionSubContent>
          )}
        </EventCompletionContent>

        <EventCompletionFooter>
          {overridable && override && (
            <LoadingButton
              variant="contained"
              size="large"
              disabled={!(selectedOverrideReasons.length > 0 && canCompleteEvent)}
              loading={completeStatus === 'LOADING'}
              type="submit"
              sx={{ mb: 3, width: { xs: '100%', sm: '45%' } }}
              onClick={handleCompleteEvent}
            >
              Confirm & Continue
            </LoadingButton>
          )}
          <Button
            variant="outlined"
            size="large"
            sx={{ mb: 3, width: { xs: '100%', sm: '45%' } }}
            onClick={handleClose}
          >
            Back to Event Details
          </Button>
        </EventCompletionFooter>
      </Container>
    </Dialog>
  );
}

export default React.memo(
  EventValidationModal,
  (prevProps: IProps, nextProps: IProps): boolean =>
    prevProps.eventId === nextProps.eventId && prevProps.completeStatus === nextProps.completeStatus
);
